import React from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { selectEnabledApps } from 'redux/enabledApps/enabledAppsSlice';
import { AppId } from 'constants/apps';
import SubscribePrompt from 'pages/subscribePrompt/SubscribePrompt';

interface AppEnabledGuardProps {
	appId: AppId;
	children: React.ReactNode;
}

// Gates a route on instance-level app enablement. Fail-closed: until the
// enablement map has loaded, render a spinner; once loaded, an app is allowed
// only if its entry is explicitly true. Absent or false -> SubscribePrompt.
const AppEnabledGuard: React.FC<AppEnabledGuardProps> = ({ appId, children }) => {
	const { enabledApps, isLoaded } = useSelector(selectEnabledApps);

	// Local development bypass: when running on localhost, skip enablement
	// gating entirely so every app is accessible regardless of the tenant map.
	if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
		return <>{children}</>;
	}

	if (!isLoaded) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (enabledApps[appId] === true) {
		return <>{children}</>;
	}

	return <SubscribePrompt appId={appId} />;
};

export default AppEnabledGuard;
